import { beMessApiGetAllMessages } from 'Api/BeMessApi'
import {
  MESSAGE_TYPE_CONFIRMATION,
  MESSAGE_TYPE_GENERIC,
  MESSAGE_TYPE_WELCOME,
  SERVICE_TYPE_CONSOLE,
  SERVICE_TYPE_ONETOMANY,
  STORE_KEY_ID_LIST
} from 'Constants/'
import getIntl from 'Helpers/intl'
import { storeGet } from 'Helpers/localStorage'
import { isUnsignedInteger } from 'mailup-react-helpers'
import { MESSAGE_BOX_VARIANT_DANGER } from 'mailup-web-components'
import { useEffect } from 'react'
import {
  atom,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState
} from 'recoil'

export const tableColumns = intl => [
  {
    field: 'Subject',
    label: intl.formatMessage({
      id: 'Load.SelectEmailMessage.SubjectHeaderColumn'
    }),
    sortable: true,
    span: 8
  },
  {
    field: 'LastUpdated',
    label: intl.formatMessage({
      id: 'Load.WidgetLastEditedMessage.LastModifiedLabel'
    }),
    sortable: true,
    span: 4
  }
]

export const modalEmailMessagesPaginationState = atom({
  key: 'modalEmailMessagesPaginationState',
  default: {
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0
  }
})

export const modalEmailMessagesSearchState = atom({
  key: 'modalEmailMessagesSearchState',
  default: {
    searchString: '',
    orderBy: 'LastUpdated',
    order: 'DESC'
  }
})

export const modalEmailMessagesState = atom({
  key: 'modalEmailMessagesState',
  default: {
    items: undefined,
    loading: true,
    loaded: false,
    error: false,
    errMessage: '',
    severity: MESSAGE_BOX_VARIANT_DANGER,
    selectedMessage: undefined,
    resetIsRequired: false,
    show: false,
    droppableId: '',
    selectedItem: undefined,
    fetchAfterCopy: false,
    showEmailLoader: false,
    // messaggi già selezionati
    // sarà confrontato l'idMessage
    // per non usare questa funzionalità passare []
    selectedMessages: []
  }
})

export async function fetchEmailMessages(
  pageNumber,
  pageSize,
  searchString,
  orderBy
) {
  const idList = storeGet(STORE_KEY_ID_LIST)
  let filterBy = `(Archived == false) &&  ((ServiceType == ${SERVICE_TYPE_CONSOLE}) || (ServiceType == ${SERVICE_TYPE_ONETOMANY})) && ((MessageType == ${MESSAGE_TYPE_GENERIC}) || (MessageType == ${MESSAGE_TYPE_CONFIRMATION}) || (MessageType == ${MESSAGE_TYPE_WELCOME})) `
  if (searchString && searchString !== '') {
    const trimmedSearchText = searchString
      .trim()
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
    const id = isUnsignedInteger(trimmedSearchText)
      ? ` || Id == ${trimmedSearchText}`
      : ''
    filterBy += ` && (Subject.Contains('${trimmedSearchText}') || Note.Contains('${trimmedSearchText}') ${id})`
  }

  const { Items, TotalItems } = await beMessApiGetAllMessages(
    idList,
    filterBy,
    orderBy,
    pageNumber,
    pageSize
  )
  return { Items, TotalItems }
}

const useModalEmailMessages = () => {
  const [
    {
      show,
      items,
      loaded,
      loading,
      error,
      errMessage,
      severity,
      resetIsRequired,
      fetchAfterCopy
    },
    setEmailState
  ] = useRecoilState(modalEmailMessagesState)

  const resetEmailPagination = useResetRecoilState(
    modalEmailMessagesPaginationState
  )
  const resetEmailSearch = useResetRecoilState(modalEmailMessagesSearchState)

  const { pageNumber, pageSize } = useRecoilValue(
    modalEmailMessagesPaginationState
  )
  const { searchString, order, orderBy } = useRecoilValue(
    modalEmailMessagesSearchState
  )

  const fetchEmailCallback = useRecoilCallback(
    ({ snapshot, set }) => async action => {
      set(modalEmailMessagesState, prev => ({
        ...prev,
        loading: true,
        fetchAfterCopy: false // reset
      }))

      const { pageNumber, pageSize } = await snapshot.getPromise(
        modalEmailMessagesPaginationState
      )
      const { searchString, orderBy, order } = await snapshot.getPromise(
        modalEmailMessagesSearchState
      )

      try {
        const itemList = await fetchEmailMessages(
          pageNumber,
          pageSize,
          searchString,
          `${orderBy} ${order}`
        )

        set(modalEmailMessagesState, prev => ({
          ...prev,
          items: itemList.Items
        }))
        set(modalEmailMessagesPaginationState, prev => ({
          ...prev,
          totalItems: itemList.TotalItems
        }))
      } catch (error) {
        set(modalEmailMessagesState, prev => ({
          ...prev,
          error: true,
          errMessage: getIntl().formatMessage({
            id: 'Load.Alerts.ErrorGeneric'
          }),
          severity: MESSAGE_BOX_VARIANT_DANGER
        }))
      }

      set(modalEmailMessagesState, prev => ({
        ...prev,
        loading: false,
        loaded: true
      }))
    },
    []
  )

  useEffect(() => {
    !loaded && fetchEmailCallback()
  }, [])

  useEffect(() => {
    if (!show && resetIsRequired) {
      resetEmailPagination()
      resetEmailSearch()
    }
  }, [show])

  useEffect(() => {
    fetchAfterCopy && fetchEmailCallback()
  }, [fetchAfterCopy])

  useEffect(() => {
    loaded && show && fetchEmailCallback()
    setEmailState(prev => ({
      ...prev,
      resetIsRequired: loaded
    }))
  }, [searchString, pageSize, pageNumber, order, orderBy, show])

  return {
    show,
    items,
    loaded,
    loading,
    error,
    errMessage,
    severity
  }
}

export default useModalEmailMessages
